// Librairies
import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { GoogleMapsModule } from '@angular/google-maps';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MapThemeDirective } from 'src/app/directives/map-theme.directive';
import { addYears, format, isSameDay, parseISO } from 'date-fns';
import { map } from 'rxjs';

// Services
import { Pop } from 'src/app/classes/shared/pop';
import { CompanyService } from 'src/app/services/api/companies/company.service';
import { LabelProjectService } from 'src/app/services/api/labelProjects/labelProject.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { CompanyStore } from 'src/app/services/stores/company.store';

// Interfaces
import { CompanyDetail, CompanyProject, CompanyUser, NamedRef, ReportDiffusion, Tenant } from 'src/app/interfaces/api/company';
import { ProjectStatus } from 'src/app/interfaces/api/project';

// Components
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { TooltipComponent } from 'projects/ui-library/src/lib/components/tooltip/tooltip.component';
import { UserAvatarComponent } from 'projects/ui-library/src/lib/components/user-avatar/user-avatar.component';
import { CompanyDrawerComponent } from 'src/app/components/main-pages/admin/companies/company-drawer/company-drawer.component';
import { CompanyLogoComponent } from 'src/app/components/shared/company-logo/company-logo.component';
import { ButtonComponent, SpinnerComponent } from 'ui-library';
import { TenantDialogComponent, TenantDialogData } from './tenant-dialog/tenant-dialog.component';

export type ProjectState = 'running' | 'upcoming' | 'finished' | 'archived' | 'frozen';

export interface ProjectRow {
  project: CompanyProject;
  state: ProjectState;
  progress: number | null;  // % of the start → end period elapsed
  location: string;
  status?: ProjectStatus;
}

export type AttentionKind = 'overdue' | 'silent' | 'archivedDevices' | 'incomplete';

export interface Attention {
  kind: AttentionKind;
  rows: ProjectRow[];
}

export interface FleetRow {
  row: ProjectRow;
  connected: number;
  count: number;
  days: number | null;   // since the last data
}

export type DiffusionState = 'none' | 'off' | 'on';

export interface DiffusionRow {
  row: ProjectRow;
  state: DiffusionState;
  reports: ReportDiffusion;
}

const NO_DIFFUSION: ReportDiffusion = { daily: 0, weekly: 0, monthly: 0, recipients: 0 };

export interface ServiceUsage {
  name: string;
  rows: ProjectRow[];
}

export type PinState = 'running' | 'upcoming' | 'finished';

export interface SitePin {
  row: ProjectRow;
  state: PinState;
  lat: number;
  lng: number;
}

export interface TimelineRow {
  row: ProjectRow;
  left: number;      // % of the period shown
  width: number;     // %
  guessed: boolean;  // dates left as created
}

export interface Timeline {
  rows: TimelineRow[];
  ticks: { label: number; left: number }[];   // years
  today: number | null;                        // %
}

export interface StreamRow {
  row: ProjectRow;
  played: number;   // seconds over 30 days
  share: number;    // % of the most watched site
  dailyLimit: number;   // seconds a day, 0 = unlimited
}

const DAY = 24 * 3600 * 1000;
const SILENT_DAYS = 30;

const daysSince = (date: string | undefined, now: number): number | null =>
  date && !Number.isNaN(Date.parse(date)) ? Math.max(0, Math.floor((now - Date.parse(date)) / DAY)) : null;

// What to settle on the portfolio: overdue sites, silent devices, devices left on archived sites, incomplete records.
export function attentions(rows: ProjectRow[], now: number): Attention[] {
  const live = rows.filter(row => row.state === 'running' || row.state === 'finished');
  const silent = (row: ProjectRow) => (row.status?.unitCount ?? 0) > 0 && (daysSince(row.status?.last, now) ?? Infinity) > SILENT_DAYS;

  const list: Attention[] = [
    { kind: 'overdue', rows: rows.filter(row => row.state === 'finished') },
    { kind: 'silent', rows: live.filter(silent) },
    { kind: 'archivedDevices', rows: rows.filter(row => row.state === 'archived' && (row.status?.unitCount ?? 0) > 0) },
    { kind: 'incomplete', rows: rows.filter(row => (row.state === 'running' || row.state === 'upcoming') && missingFields(row.project).length) },
  ];
  return list.filter(attention => attention.rows.length);
}

// Project record fields a client fills: site position, n° d'affaire.
export function missingFields(project: CompanyProject): ('location' | 'reference')[] {
  return [
    ...(project.lat == null || project.lon == null ? ['location' as const] : []),
    ...(project.reference ? [] : ['reference' as const]),
  ];
}

const notArchived = (row: ProjectRow) => row.state === 'running' || row.state === 'upcoming' || row.state === 'finished';

// Reports sent by the sites not archived: nothing sent first, then turned off, then the most recipients.
export function diffusion(rows: ProjectRow[]): DiffusionRow[] {
  const order: Record<DiffusionState, number> = { none: 0, off: 1, on: 2 };
  return rows
    .filter(notArchived)
    .map(row => {
      const reports = row.project.diffusion ?? NO_DIFFUSION;
      let state: DiffusionState = reports.recipients ? 'on' : 'none';
      if (row.project.reportsEnabled === false) state = 'off';
      return { row, state, reports };
    })
    .sort((a, b) => order[a.state] - order[b.state] || b.reports.recipients - a.reports.recipients);
}

// Services of the sites not archived: AI modules by number of sites, stream played over 30 days.
export function services(rows: ProjectRow[]): { modules: ServiceUsage[]; streams: StreamRow[] } {
  const live = rows.filter(notArchived);
  const modules = new Map<string, ProjectRow[]>();
  live.forEach(row => row.project.aiModules?.forEach(name => modules.set(name, [...modules.get(name) ?? [], row])));
  const streams = live.filter(row => row.project.stream?.played30Days);
  const maxPlayed = Math.max(1, ...streams.map(row => row.project.stream!.played30Days));

  return {
    modules: [...modules].map(([name, sites]) => ({ name, rows: sites })).sort((a, b) => b.rows.length - a.rows.length),
    streams: streams.map(row => ({ row, played: row.project.stream!.played30Days, share: row.project.stream!.played30Days / maxPlayed * 100, dailyLimit: row.project.stream!.dailyLimit }))
      .sort((a, b) => b.played - a.played),
  };
}

export type AdoptionBucket = 'week' | 'month' | 'quarter' | 'year' | 'dormant' | 'never';

const BUCKET_LIMITS: [AdoptionBucket, number][] = [['week', 7], ['month', 30], ['quarter', 90], ['year', 365]];
const STAFF_ROLES = new Set(['dev', 'admin']);

// Last connection of the client accounts (CAD.42 staff left out): active over 90 days, to review after a year.
export function adoption(users: CompanyUser[], now: number) {
  const clients = users.filter(user => !STAFF_ROLES.has(user.role?.name ?? ''));
  const bucketOf = (user: CompanyUser): AdoptionBucket => {
    const days = daysSince(user.lastConnection, now);
    if (days === null) return 'never';
    return BUCKET_LIMITS.find(([, limit]) => days < limit)?.[0] ?? 'dormant';
  };
  const counts = new Map<AdoptionBucket, number>();
  clients.forEach(user => counts.set(bucketOf(user), (counts.get(bucketOf(user)) ?? 0) + 1));

  const buckets = (['week', 'month', 'quarter', 'year', 'dormant', 'never'] as AdoptionBucket[]).map(key => ({ key, count: counts.get(key) ?? 0 }));
  const active = buckets.slice(0, 3).reduce((sum, bucket) => sum + bucket.count, 0);

  return {
    total: clients.length,
    active,
    activeRate: clients.length ? Math.round(active / clients.length * 100) : 0,
    buckets,
    dormant: counts.get('dormant') ?? 0,
    guests: clients.filter(user => user.role?.name === 'guest').length,
    unverified: clients.filter(user => user.verified === false).length,
  };
}

// Sites not archived on the map; the ones without a valid position apart (missing, 0/0, out of range like Lambert).
export function sitePins(rows: ProjectRow[]): { pins: SitePin[]; unplaced: ProjectRow[] } {
  const pins: SitePin[] = [];
  const unplaced: ProjectRow[] = [];

  rows.filter(notArchived).forEach(row => {
    const lat = Number(row.project.lat);
    const lng = Number(row.project.lon);
    const placed = row.project.lat != null && row.project.lon != null && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && (lat || lng);
    if (placed) pins.push({ row, state: row.state as PinState, lat, lng });
    else unplaced.push(row);
  });

  return { pins, unplaced };
}

// Dates left as created: start = end (API default), or the end one year to the day after the start (creation form).
export function defaultDates(project: CompanyProject): boolean {
  const start = Date.parse(project.startDate ?? '');
  const end = Date.parse(project.endDate ?? '');
  return end - start < DAY || isSameDay(addYears(start, 1), end);
}

// Sites not archived on one period, sorted by start; years ticked, today marked.
export function timeline(rows: ProjectRow[], now: number): Timeline {
  const dated = rows.filter(row => notArchived(row) && !Number.isNaN(Date.parse(row.project.startDate ?? '')) && !Number.isNaN(Date.parse(row.project.endDate ?? '')));
  if (!dated.length) return { rows: [], ticks: [], today: null };

  const span = (row: ProjectRow) => [Date.parse(row.project.startDate!), Math.max(Date.parse(row.project.endDate!), Date.parse(row.project.startDate!))] as const;
  const min = Math.min(...dated.map(row => span(row)[0]));
  const max = Math.max(...dated.map(row => span(row)[1]), min + DAY);
  const at = (time: number) => (time - min) / (max - min) * 100;

  const first = new Date(min).getFullYear();
  const years = Array.from({ length: new Date(max).getFullYear() - first }, (_, index) => first + index + 1);

  return {
    rows: dated
      .map(row => ({ row, left: at(span(row)[0]), width: Math.max(0.5, at(span(row)[1]) - at(span(row)[0])), guessed: defaultDates(row.project) }))
      .sort((a, b) => a.left - b.left),
    // The last labels would overflow the track.
    ticks: [{ label: first, left: 0 }, ...years.map(year => ({ label: year, left: at(new Date(year, 0, 1).getTime()) }))].filter(tick => tick.left <= 95),
    today: now >= min && now <= max ? at(now) : null,
  };
}

// Devices of the live sites, the most equipped first.
export function fleet(rows: ProjectRow[], now: number): FleetRow[] {
  return rows
    .filter(row => row.state !== 'archived' && (row.status?.unitCount ?? 0) > 0)
    .map(row => ({ row, connected: row.status!.unitConnected ?? 0, count: row.status!.unitCount, days: daysSince(row.status!.last, now) }))
    .sort((a, b) => b.count - a.count);
}

export const isSsoActive = (user: CompanyUser): boolean => user.ssoEnabled !== false && !!user.ssoLinked;

export function projectState(project: CompanyProject, now: number): ProjectState {
  if (project.frozen) return 'frozen';
  if (project.archived) return 'archived';
  if (project.startDate && Date.parse(project.startDate) > now) return 'upcoming';
  if (project.endDate && Date.parse(project.endDate) < now) return 'finished';
  return 'running';
}

export function projectProgress(project: CompanyProject, now: number): number | null {
  if (!project.startDate || !project.endDate) return null;
  const start = Date.parse(project.startDate);
  const end = Date.parse(project.endDate);
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return null;
  return Math.min(100, Math.max(0, Math.round((now - start) / (end - start) * 100)));
}

// Company page: /company/:id, or the current company on /company (header pill).
@Component({
  selector: 'app-company',
  standalone: true,
  imports: [
    DatePipe,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    ButtonComponent,
    SpinnerComponent,
    NoDataComponent,
    UserAvatarComponent,
    TooltipComponent,
    GoogleMapsModule,
    MapThemeDirective,
    CompanyLogoComponent,
    CompanyDrawerComponent
  ],
  templateUrl: './company.component.html',
  styleUrl: './company.component.scss'
})

export class CompanyComponent {
  // Services
  private readonly companyService = inject(CompanyService);
  private readonly projectService = inject(ProjectService);
  private readonly labelProjectService = inject(LabelProjectService);
  private readonly companyStore = inject(CompanyStore);
  private readonly authService = inject(AuthService);
  private readonly dialog = inject(MatDialog);
  private readonly pop = inject(Pop);
  private readonly translate = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);

  // Signals
  readonly company = signal<CompanyDetail | null>(null);
  readonly loading = signal(true);
  readonly failed = signal(false);
  readonly editing = signal(false);
  readonly statuses = signal<Record<string, ProjectStatus>>({});
  readonly isPrivileged = this.authService.isPrivileged;
  readonly canManageTenants = this.authService.canManageProject;
  readonly isSsoActive = isSsoActive;
  readonly attentionIcons: Record<AttentionKind, string> = { overdue: 'event_busy', silent: 'portable_wifi_off', archivedDevices: 'inventory_2', incomplete: 'edit_note' };

  private readonly routeId = toSignal(this.route.paramMap.pipe(map(params => params.get('id'))));
  readonly companyId = computed(() => this.routeId() ?? this.companyStore.current()?.id);
  readonly isCurrent = computed(() => this.company()?.id === this.companyStore.current()?.id);

  readonly projectRows = computed<ProjectRow[]>(() => {
    const now = Date.now();
    const statuses = this.statuses();
    return (this.company()?.projects ?? []).map(project => ({
      project,
      state: projectState(project, now),
      progress: projectProgress(project, now),
      location: [project.city, project.country].filter(Boolean).join(', '),
      status: project.database ? statuses[project.database] : undefined
    }));
  });

  readonly runningCount = computed(() => this.projectRows().filter(row => row.state === 'running').length);

  readonly attentions = computed(() => attentions(this.projectRows(), Date.now()));

  readonly fleet = computed(() => fleet(this.projectRows(), Date.now()));
  readonly archivedDevices = computed(() => this.projectRows()
    .filter(row => row.state === 'archived')
    .reduce((count, row) => count + (row.status?.unitCount ?? 0), 0));

  readonly devices = computed(() => Object.values(this.statuses()).reduce(
    (total, status) => ({ connected: total.connected + (status.unitConnected ?? 0), count: total.count + (status.unitCount ?? 0) }),
    { connected: 0, count: 0 }
  ));

  readonly roles = computed(() => {
    const counts = new Map<string, number>();
    for (const user of this.company()?.users ?? []) {
      const role = user.role?.name ?? 'user';
      counts.set(role, (counts.get(role) ?? 0) + 1);
    }
    return [...counts].map(([role, count]) => ({ role, count })).sort((a, b) => b.count - a.count);
  });

  readonly adoption = computed(() => adoption(this.company()?.users ?? [], Date.now()));

  readonly diffusion = computed(() => diffusion(this.projectRows()));
  readonly diffusionOn = computed(() => this.diffusion().filter(item => item.state === 'on').length);

  readonly services = computed(() => services(this.projectRows()));

  readonly sites = computed(() => sitePins(this.projectRows()));

  readonly timeline = computed(() => timeline(this.projectRows(), Date.now()));
  readonly guessedNames = computed(() => this.timeline().rows.filter(item => item.guessed).map(item => item.row.project.name));
  readonly pinStates: PinState[] = ['running', 'upcoming', 'finished'];
  readonly pinCounts = computed(() => Object.fromEntries(this.pinStates.map(state => [state, this.sites().pins.filter(pin => pin.state === state).length])));
  readonly unplacedNames = computed(() => this.sites().unplaced.map(row => row.project.name).join(', '));
  readonly mapOptions: google.maps.MapOptions = { center: { lat: 46.6, lng: 2.4 }, zoom: 5, streetViewControl: false, mapTypeControl: false };
  // Read only inside the @defer map block: browser side, Google Maps loaded.
  readonly pinOptions = computed(() => {
    const css = getComputedStyle(document.body);
    const colors: Record<PinState, string> = {
      running: css.getPropertyValue('--primary-color').trim() || '#00b09b',
      upcoming: css.getPropertyValue('--info-color').trim() || '#1e88e5',
      finished: '#f39c12',
    };
    return Object.fromEntries(this.sites().pins.map(pin => [pin.row.project.id, {
      icon: { path: google.maps.SymbolPath.CIRCLE, scale: 7, fillColor: colors[pin.state], fillOpacity: 0.9, strokeColor: '#ffffff', strokeWeight: 1 },
    } as google.maps.MarkerOptions]));
  });
  readonly hoveredPin = signal<{ pin: SitePin; x: number; y: number } | null>(null);
  readonly aiCovered = computed(() => new Set(this.services().modules.flatMap(module => module.rows)).size);
  // AI module labels, as in the project settings.
  private readonly moduleNames = computed(() => new Map(this.labelProjectService.labelProjectsSignal()
    .map(labelProject => [labelProject.name, labelProject.displayName || labelProject.name])));

  constructor() {
    effect(() => {
      const id = this.companyId();
      untracked(() => this.load(id));
    });

    // Any write on the company shown, from this page or another one (kept alive by the route reuse): reloaded.
    effect(() => {
      const change = this.companyService.changed();
      untracked(() => {
        if (change && change.id === this.company()?.id) this.load();
      });
    });
  }

  // Chip tooltip: the end date passed, the last data, the devices left, the fields missing.
  attentionDetail(kind: AttentionKind, row: ProjectRow): string {
    switch (kind) {
      case 'overdue': return this.translate.instant('companies.attention_detail_overdue', { date: row.project.endDate ? format(parseISO(row.project.endDate), 'dd/MM/yyyy') : '' });
      case 'silent': return this.lastData(daysSince(row.status?.last, Date.now()));
      case 'archivedDevices': return this.translate.instant('companies.attention_detail_devices', { count: row.status?.unitCount ?? 0 });
      case 'incomplete': return missingFields(row.project).map(field => this.translate.instant('companies.missing_' + field)).join(', ');
    }
  }

  // Frames every site; a single one is centered.
  fitSites(map: google.maps.Map): void {
    const pins = this.sites().pins;
    if (!pins.length) return;
    if (pins.length === 1) {
      map.setCenter({ lat: pins[0]!.lat, lng: pins[0]!.lng });
      map.setZoom(10);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    pins.forEach(pin => bounds.extend({ lat: pin.lat, lng: pin.lng }));
    map.fitBounds(bounds, 40);
  }

  hoverPin(pin: SitePin, event: google.maps.MapMouseEvent): void {
    const mouse = event.domEvent as MouseEvent;
    if (mouse?.clientX !== undefined) this.hoveredPin.set({ pin, x: mouse.clientX, y: mouse.clientY });
  }

  period(item: TimelineRow): string {
    const day = (date: string) => format(parseISO(date), 'dd/MM/yyyy');
    const dates = `${day(item.row.project.startDate!)} → ${day(item.row.project.endDate!)}`;
    return item.guessed ? `${dates} · ${this.translate.instant('companies.timeline_guessed_hint')}` : dates;
  }

  moduleName(name: string): string {
    return name === 'nelia' ? 'Nelia' : this.moduleNames().get(name) ?? name;
  }

  moduleSites(module: ServiceUsage): string {
    return module.rows.map(row => row.project.name).join(', ');
  }

  // 7 500 s → "2 h 05", 900 s → "15 min".
  duration(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.round(seconds % 3600 / 60);
    return hours ? `${hours} h ${String(minutes).padStart(2, '0')}` : `${minutes} min`;
  }

  lastData(days: number | null): string {
    if (days === null) return this.translate.instant('companies.fleet_never');
    return days === 0 ? this.translate.instant('companies.fleet_today') : this.translate.instant('companies.fleet_days', { days });
  }

  // Under a week: fresh; under a month: late; beyond: silent.
  freshness(days: number | null): string {
    if (days === null || days > SILENT_DAYS) return 'silent';
    return days > 7 ? 'late' : 'fresh';
  }

  activate(): void {
    const company = this.company();
    if (!company) return;
    this.companyStore.select(company);
    this.pop.success(this.translate.instant('companies.activated', { name: company.name }));
  }

  load(id = this.companyId()): void {
    this.failed.set(false);
    if (!id) {
      this.company.set(null);
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.companyService.getCompany(id).subscribe({
      next: company => {
        this.company.set(company);
        this.loading.set(false);
        this.loadStatuses(company);
        if (company.projects.some(project => project.aiModules?.length)) this.labelProjectService.getLabelProjects({}).subscribe({ error: () => {} });
      },
      error: () => {
        this.loading.set(false);
        this.failed.set(true);
      }
    });
  }

  openTenant(tenant?: Tenant<NamedRef>): void {
    const data: TenantDialogData = { company: this.company()!, tenant };
    this.dialog.open(TenantDialogComponent, { data, width: '560px', maxWidth: '95vw', autoFocus: false })
      .afterClosed()
      .subscribe(saved => {
        if (!saved) return;
        this.pop.success(this.translate.instant(tenant ? 'companies.tenant_saved' : 'companies.tenant_created'));
      });
  }

  removeTenant(tenant: Tenant<NamedRef>): void {
    const companyId = this.company()!.id;
    this.pop.confirm(this.translate.instant('companies.tenant_delete_confirm', { name: tenant.name }), (confirm: { ok: boolean }) => {
      if (!confirm.ok) return;
      this.companyService.removeTenant(companyId, tenant.id).subscribe({
        next: () => {
          this.pop.success(this.translate.instant('companies.tenant_deleted'));
        },
        error: () => this.pop.error(this.translate.instant('companies.errors.generic'))
      });
    }, 'form.delete');
  }

  // Devices per project; skipped for CAD.42, whose project list is implicit.
  private loadStatuses(company: CompanyDetail): void {
    const databases = company.projects.map(project => project.database).filter(Boolean);
    if (company.allProjects || !databases.length) return this.statuses.set({});

    this.projectService.getStatus(databases.join(',')).subscribe({
      next: (statuses: ProjectStatus[]) => this.statuses.set(Object.fromEntries(statuses.map(status => [status.database, status]))),
      error: () => this.statuses.set({})
    });
  }
}
